// Dependencies
import { z } from "zod";

import { coercedBoolean } from "@performyard/utils-isomorphic";

// local imports
import request from "../base-request";

const sideNavThemeSchema = z.object({
	id: z.string(),
	name: z.string(),
	backgroundColor: z.string(),
	foregroundColor: z.string(),
	isDefault: coercedBoolean,
});

export const SIDE_NAV_AREAS = [
	"performance",
	"company",
	"goals",
	"reviews",
	"feedback",
	"meeting-workspaces",
	"engagement",
	"surveys",
	"analytics",
	"reporting",
	"compensation",
	"administration",
] as const;

export const SIDE_NAV_TAGS = [
	"dashboard",
	"reviews",
	"goals",
	"feedback",
	"documents",
	"directory",
	"org-chart",
	"workflows",
	"meetings",
	"resources",
	"list",
	"alignment",
	"employees-without-goals",
	"forms-i-need-to-do",
	"forms-i-did",
	"reviews-of-me",
	"reviews-of-my-team",
	"review-cycles",
	"all-reviews",
	"all-forms",
	"assignments",
	"performance-graphs",
	"received",
	"given",
	"team",
	"notes",
	"requests",
	"outgoing",
	"overview",
	"cohorts",
	"factors",
	"questions",
	"enps",
] as const;

export type SideNavArea = (typeof SIDE_NAV_AREAS)[number];
export type SideNavTag = (typeof SIDE_NAV_TAGS)[number];
export type VisibleNavTags = Partial<Record<SideNavArea, SideNavTag[]>> & Record<string, string[]>;

const visibleNavTagsSchema = z.record(z.string(), z.array(z.string())).transform((tags) => tags as VisibleNavTags);

export const navigationConfigSchema = z.object({
	currentUserId: z.string(),
	currentUserName: z.string(),
	currentUserJobTitle: z.string(),
	currentUserEmail: z.string().optional().default(""),
	companyId: z.string(),
	theUserId: z.string(),
	isViewingSelf: coercedBoolean,
	currentNav: z.string().nullable(),
	isAdmin: coercedBoolean,
	isBasicAdmin: coercedBoolean,
	isEngagementOnly: coercedBoolean,
	isOverlord: coercedBoolean,
	hasDirectReports: coercedBoolean.optional().default(false),
	possessor: z
		.object({
			name: z.string(),
			id: z.string(),
		})
		.nullable(),
	visibleNavTags: visibleNavTagsSchema.optional(),
	navColor: z.string().optional().default(""),
	iconColor: z.string().optional().default(""),
	preferredNavigation: z.enum(["classic", "new"]).optional().default("classic"),
	sideNavTheme: sideNavThemeSchema.optional(),
	currentPromotion: z.record(z.string(), z.unknown()).optional().default({}),
	logoUrl: z.string().optional().default(""),
	mobileLogoUrl: z.string().optional().default(""),
});

export const navigationConfigResponseSchema = z.object({
	navigationConfig: navigationConfigSchema,
});

export type NavigationConfig = z.infer<typeof navigationConfigSchema>;
export type NavigationConfigResponse = z.infer<typeof navigationConfigResponseSchema>;

/**
 * Get navigation config for current viewing context.
 * Optionally pass employeeId to get config for viewing a specific employee.
 */
export const getNavigationConfig = async (employeeId?: string) => {
	const url = employeeId ? `/api/navigation-config?employeeId=${employeeId}` : "/api/navigation-config";
	const [data] = await request(url);
	return navigationConfigResponseSchema.parse(data);
};
